import React, { useState, useEffect, useCallback } from 'react';
import {
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  FileText,
  AlertCircle,
  Printer,
  BookOpen,
  CheckCircle,
  Calendar,
  X
} from 'lucide-react';
import api from '../api';

// Loading Spinner Icon
const Spinner = ({ className }) => (
  <svg className={`animate-spin ${className}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

const TransactionHistory = () => {
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    fromDate: '',
    toDate: ''
  });
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [institutionData, setInstitutionData] = useState({
    name: '',
    logoUrl: ''
  });

  const tenantId = localStorage.getItem('tenantId');

  // ============ FETCH INSTITUTION DATA ============
  const fetchInstitutionData = useCallback(async () => {
    try {
      if (!tenantId) return;
      
      const response = await api.get(`/api/organization/basic-info/${tenantId}`);
      if (response.data?.success) {
        setInstitutionData({
          name: response.data.name || '',
          logoUrl: response.data.logoUrl || ''
        });
      }
    } catch (err) {
      console.error('Error fetching institution data:', err);
      // Try to get from localStorage
      try {
        const stored = localStorage.getItem('tenantData');
        if (stored) {
          const parsed = JSON.parse(stored);
          setInstitutionData({
            name: parsed.institutionName || '',
            logoUrl: parsed.logoUrl || ''
          });
        }
      } catch (e) {
        console.error('Error reading from localStorage:', e);
      }
    }
  }, [tenantId]);

  // ============ FETCH TRANSACTION RECORDS ============
  const fetchTransactionHistory = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        tenantId,
        page,
        limit: pagination.limit
      };

      if (filters.status !== 'all') params.status = filters.status;
      if (filters.search) params.search = filters.search;
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;

      const response = await api.get('/api/transactions', { params });
      if (response.data.success) {
        setTransactions(response.data.data);
        setPagination((prev) => ({
          ...prev,
          page,
          total: response.data.pagination.total,
          pages: response.data.pagination.pages
        }));
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Could not load circulation history records');
    } finally {
      setLoading(false);
    }
  }, [tenantId, filters, pagination.limit]);

  // ============ GET STATUS BADGE CONFIG ============
  const getStatusBadge = (status) => {
    const statusMap = {
      'issued': { 
        label: 'Currently Borrowed', 
        color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300 border-blue-200 dark:border-blue-800', 
        icon: BookOpen 
      },
      'returned': { 
        label: 'Returned', 
        color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800', 
        icon: CheckCircle 
      },
      'overdue': { 
        label: 'Overdue', 
        color: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300 border-rose-200 dark:border-rose-800', 
        icon: AlertCircle 
      },
      'lost': { 
        label: 'Reported Lost', 
        color: 'bg-slate-200 text-slate-800 dark:bg-gray-700 dark:text-slate-200 border-slate-300 dark:border-gray-600', 
        icon: AlertCircle 
      }
    };
    return statusMap[status] || { 
      label: status, 
      color: 'bg-slate-100 text-slate-700 dark:bg-gray-700 dark:text-slate-300 border-slate-200', 
      icon: FileText 
    };
  };

  // ============ PRINT SLIP RECEIPT ============
  const printReceipt = (transaction) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please enable browser popups to generate print slips');
      return;
    }

    const status = getStatusBadge(transaction.status);
    const institutionName = institutionData.name || 'Educational Institution';
    const logoUrl = institutionData.logoUrl;

    // Get current date and time
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const timeStr = now.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

    const content = `
      <html>
        <head>
          <title>Transaction Receipt</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; 
              padding: 30px; 
              max-width: 600px; 
              margin: 0 auto; 
              color: #0f172a; 
              background: #ffffff;
            }
            .header { 
              text-align: center; 
              border-bottom: 2px solid #e2e8f0; 
              padding-bottom: 15px; 
              margin-bottom: 20px; 
            }
            .header-logo { 
              display: flex; 
              align-items: center; 
              justify-content: center; 
              gap: 12px; 
              margin-bottom: 6px; 
            }
            .header-logo img { 
              max-height: 50px; 
              width: auto; 
              object-fit: contain; 
            }
            .header-logo .logo-placeholder { 
              width: 50px; 
              height: 50px; 
              background: linear-gradient(135deg, #4f46e5, #3b82f6); 
              border-radius: 12px; 
              display: flex; 
              align-items: center; 
              justify-content: center; 
              color: white; 
              font-size: 24px; 
              font-weight: 900; 
              flex-shrink: 0; 
            }
            .header h1 { 
              margin: 0; 
              font-size: 18px; 
              color: #1e293b; 
              font-weight: 800; 
              letter-spacing: -0.025em; 
            }
            .header .subtitle { 
              font-size: 11px; 
              color: #64748b; 
              font-weight: 500; 
              letter-spacing: 0.05em; 
              text-transform: uppercase; 
            }
            .header .timestamp { 
              font-size: 10px; 
              color: #94a3b8; 
              margin-top: 3px; 
            }
            .section-title { 
              font-size: 10px; 
              font-weight: 700; 
              text-transform: uppercase; 
              letter-spacing: 0.08em; 
              color: #64748b; 
              margin: 16px 0 6px 0; 
              border-bottom: 1px dashed #e2e8f0; 
              padding-bottom: 4px; 
            }
            .card { 
              padding: 10px 12px; 
              background: #f8fafc; 
              border: 1px solid #e2e8f0; 
              border-radius: 8px; 
              margin-bottom: 8px; 
              font-size: 12px; 
            }
            .row { 
              display: flex; 
              justify-content: space-between; 
              padding: 3px 0; 
            }
            .label { 
              font-weight: 600; 
              color: #475569; 
            }
            .value { 
              font-weight: 500; 
              color: #0f172a; 
              text-align: right; 
              max-width: 60%; 
            }
            .status-badge { 
              display: inline-block; 
              padding: 2px 10px; 
              border-radius: 9999px; 
              font-size: 10px; 
              font-weight: 700; 
              text-transform: uppercase; 
              letter-spacing: 0.03em; 
            }
            .status-issued { background: #dbeafe; color: #1e40af; }
            .status-returned { background: #d1fae5; color: #065f46; }
            .status-overdue { background: #fee2e2; color: #991b1b; }
            .status-lost { background: #f1f5f9; color: #334155; }
            .fine-positive { color: #dc2626; font-weight: 700; }
            .fine-zero { color: #16a34a; font-weight: 700; }
            .barcode-tag { 
              font-family: "Courier New", monospace; 
              background: #e2e8f0; 
              padding: 1px 6px; 
              border-radius: 4px; 
              font-size: 11px; 
            }
            .footer { 
              text-align: center; 
              margin-top: 20px; 
              padding-top: 15px; 
              border-top: 2px solid #e2e8f0; 
              color: #94a3b8; 
              font-size: 10px; 
            }
            .footer .brand { 
              font-weight: 700; 
              color: #4f46e5; 
              font-size: 11px; 
            }
            .footer .brand-sub { 
              font-size: 9px; 
              color: #94a3b8; 
            }
            .footer a { 
              color: #4f46e5; 
              text-decoration: none; 
            }
            .print-date { 
              text-align: center; 
              font-size: 9px; 
              color: #94a3b8; 
              margin-top: 4px; 
            }
            @media print {
              body { padding: 20px; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="header-logo">
              ${logoUrl ? `
                <img src="${logoUrl}" alt="${institutionName}" onerror="this.style.display='none'" />
              ` : `
                <div class="logo-placeholder">${institutionName.charAt(0).toUpperCase()}</div>
              `}
              <div>
                <h1>${institutionName}</h1>
                <div class="subtitle">Library Transaction Slip</div>
              </div>
            </div>
            <div class="timestamp">Printed on: ${dateStr} at ${timeStr}</div>
          </div>

          <div class="section-title">📖 Book Information</div>
          <div class="card">
            <div class="row"><span class="label">Title</span><span class="value" style="font-weight:700;">${transaction.bookTitle || 'N/A'}</span></div>
            <div class="row"><span class="label">ISBN</span><span class="value">${transaction.bookISBN || 'N/A'}</span></div>
            <div class="row"><span class="label">Copy Barcode</span><span class="value"><span class="barcode-tag">${transaction.bookBarcode || 'N/A'}</span></span></div>
          </div>

          <div class="section-title">👤 Borrower Details</div>
          <div class="card">
            <div class="row"><span class="label">Name</span><span class="value">${transaction.memberName || 'Unknown'}</span></div>
            <div class="row"><span class="label">Email</span><span class="value">${transaction.memberEmail || 'N/A'}</span></div>
            <div class="row"><span class="label">USN / ID</span><span class="value">${transaction.memberUSN || 'N/A'}</span></div>
          </div>

          <div class="section-title">📅 Circulation Details</div>
          <div class="card">
            <div class="row"><span class="label">Issued On</span><span class="value">${new Date(transaction.issueDate).toLocaleDateString('en-IN')}</span></div>
            <div class="row"><span class="label">Return Due Date</span><span class="value" style="${(transaction.status === 'overdue' || (transaction.status === 'issued' && new Date(transaction.dueDate) < new Date())) ? 'color:#dc2626;font-weight:700;' : ''}">${new Date(transaction.dueDate).toLocaleDateString('en-IN')}</span></div>
            ${transaction.returnDate ? `<div class="row"><span class="label">Returned On</span><span class="value">${new Date(transaction.returnDate).toLocaleDateString('en-IN')}</span></div>` : ''}
            <div class="row">
              <span class="label">Status</span>
              <span class="value"><span class="status-badge status-${transaction.status}">${status.label}</span></span>
            </div>
            ${transaction.renewalCount > 0 ? `<div class="row"><span class="label">Extensions</span><span class="value">Extended ${transaction.renewalCount} time(s)</span></div>` : ''}
          </div>

          <div class="section-title">💰 Fine Assessment</div>
          <div class="card">
            <div class="row">
              <span class="label">Total Fine</span>
              <span class="value ${transaction.fineAmount > 0 ? 'fine-positive' : 'fine-zero'}">${transaction.fineAmount > 0 ? `₹${transaction.fineAmount}` : 'No Fine'}</span>
            </div>
            ${transaction.fineAmount > 0 ? `
              <div class="row"><span class="label">Payment Status</span><span class="value">${transaction.finePaid ? '✅ Paid' : '⏳ Pending'}</span></div>
              ${transaction.finePaidAt ? `<div class="row"><span class="label">Paid On</span><span class="value">${new Date(transaction.finePaidAt).toLocaleDateString('en-IN')}</span></div>` : ''}
            ` : ''}
            ${transaction.daysLate > 0 ? `<div class="row"><span class="label">Days Overdue</span><span class="value" style="color:#dc2626;font-weight:700;">${transaction.daysLate} days</span></div>` : ''}
          </div>

          ${transaction.notes ? `
            <div class="section-title">📝 Counter Notes</div>
            <div class="card">
              <p style="margin: 0; font-style: italic; font-size: 11px; color: #475569;">${transaction.notes}</p>
            </div>
          ` : ''}

          <div class="footer">
            <div class="brand">Truyon ERP</div>
            <div class="brand-sub">Library Management System</div>
            <div style="margin-top: 6px;">
              <a href="https://truyon.com" target="_blank" rel="noreferrer">truyon.com</a>
            </div>
            <div class="print-date">Transaction ID: ${transaction._id || 'N/A'}</div>
          </div>

          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.close();
              }, 500);
            }
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(content);
    printWindow.document.close();
  };

  // ============ EFFECTS ============
  useEffect(() => {
    if (tenantId) {
      fetchInstitutionData();
      fetchTransactionHistory(1);
    }
  }, [tenantId, fetchInstitutionData, fetchTransactionHistory]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (tenantId) fetchTransactionHistory(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [filters, tenantId, fetchTransactionHistory]);

  const clearFilters = () => {
    setFilters({ status: 'all', search: '', fromDate: '', toDate: '' });
    fetchTransactionHistory(1);
  };

  return (
    <div className="space-y-4">
      
      {/* Search & Filter Toolbar */}
      <div className="bg-slate-50 dark:bg-gray-900/40 p-3.5 sm:p-4 rounded-xl border border-slate-200 dark:border-gray-700 space-y-3">
        
        {/* Search Bar & Primary Actions */}
        <div className="flex flex-col md:flex-row gap-2.5">
          <div className="flex-1 relative">
            <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input 
              type="text" 
              placeholder="Search by title, student USN, borrower name, or barcode..." 
              value={filters.search} 
              onChange={(e) => setFilters({ ...filters, search: e.target.value })} 
              className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
            />
            {filters.search && (
              <button
                onClick={() => setFilters({ ...filters, search: '' })}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <select 
              value={filters.status} 
              onChange={(e) => setFilters({ ...filters, status: e.target.value })} 
              className="flex-1 md:w-40 px-3 py-2.5 rounded-xl border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 font-medium"
            >
              <option value="all">All Statuses</option>
              <option value="issued">Currently Borrowed</option>
              <option value="returned">Returned</option>
              <option value="overdue">Overdue</option>
              <option value="lost">Reported Lost</option>
            </select>

            <button 
              onClick={() => fetchTransactionHistory(pagination.page)} 
              className="px-3.5 py-2.5 bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white rounded-xl text-xs sm:text-sm font-semibold transition-all flex items-center justify-center space-x-1.5 shadow-sm shadow-purple-500/20 flex-shrink-0"
            >
              <RefreshCw className="h-4 w-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Date Filter Range */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-1">
          <div className="flex items-center space-x-2 flex-1">
            <Calendar className="h-4 w-4 text-slate-400 flex-shrink-0" />
            <input 
              type="date" 
              value={filters.fromDate} 
              onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })} 
              className="w-full px-3 py-1.5 rounded-lg border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <span className="text-xs text-slate-400 font-medium">to</span>
            <input 
              type="date" 
              value={filters.toDate} 
              onChange={(e) => setFilters({ ...filters, toDate: e.target.value })} 
              className="w-full px-3 py-1.5 rounded-lg border border-slate-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-xs focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {(filters.search || filters.status !== 'all' || filters.fromDate || filters.toDate) && (
            <button
              onClick={clearFilters}
              className="self-end sm:self-auto px-2.5 py-1 text-xs font-semibold text-purple-600 dark:text-purple-400 hover:underline flex items-center space-x-1"
            >
              <X className="h-3.5 w-3.5" />
              <span>Reset Filters</span>
            </button>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16 space-y-3">
          <Spinner className="h-8 w-8 text-purple-600" />
          <p className="text-xs sm:text-sm font-semibold text-slate-500 dark:text-slate-400">Loading circulation log...</p>
        </div>
      ) : error ? (
        <div className="p-8 text-center bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700">
          <AlertCircle className="h-10 w-10 text-rose-500 mx-auto mb-3" />
          <p className="text-sm font-semibold text-rose-600 dark:text-rose-400">{error}</p>
          <button 
            onClick={() => fetchTransactionHistory(1)} 
            className="mt-3 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-semibold transition-colors"
          >
            Retry
          </button>
        </div>
      ) : transactions.length > 0 ? (
        <>
          {/* Mobile Card List View (< md screen) */}
          <div className="block md:hidden space-y-3">
            {transactions.map((tx) => {
              const status = getStatusBadge(tx.status);
              const StatusIcon = status.icon;
              const isOverdue = tx.status === 'overdue' || (tx.status === 'issued' && new Date(tx.dueDate) < new Date());

              return (
                <div 
                  key={tx._id}
                  className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-slate-200 dark:border-gray-700 space-y-3 shadow-sm hover:border-purple-300 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-bold text-gray-900 dark:text-white text-sm truncate">{tx.bookTitle || 'Untitled Book'}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-mono mt-0.5">
                        Barcode: {tx.bookBarcode || 'N/A'}
                      </p>
                    </div>

                    <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border flex items-center space-x-1 flex-shrink-0 ${status.color}`}>
                      <StatusIcon className="h-3 w-3" />
                      <span>{status.label}</span>
                    </span>
                  </div>

                  <div className="p-2.5 bg-slate-50 dark:bg-gray-700/30 rounded-lg text-xs space-y-1">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Borrower:</span>
                      <span className="font-semibold text-gray-900 dark:text-white truncate max-w-[180px]">{tx.memberName || 'Unknown'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">USN / ID:</span>
                      <span className="font-mono text-slate-700 dark:text-slate-300">{tx.memberUSN || tx.memberEmail || 'N/A'}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-slate-400">Issue Date</span>
                      <p className="font-medium text-slate-800 dark:text-slate-200">{new Date(tx.issueDate).toLocaleDateString('en-IN')}</p>
                    </div>
                    <div>
                      <span className="text-slate-400">Return Due</span>
                      <p className={`font-semibold ${isOverdue ? 'text-rose-600' : 'text-slate-800 dark:text-slate-200'}`}>
                        {new Date(tx.dueDate).toLocaleDateString('en-IN')}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-gray-700">
                    <div className="flex items-center text-xs">
                      <span className="text-slate-500 mr-1.5">Fine:</span>
                      <span className={`font-bold ${tx.fineAmount > 0 ? 'text-rose-600' : 'text-slate-400'}`}>
                        {tx.fineAmount > 0 ? `₹${tx.fineAmount}` : '₹0'}
                      </span>
                      {tx.finePaid && <span className="ml-1 text-emerald-600 font-semibold">(Paid)</span>}
                    </div>

                    <button
                      onClick={() => printReceipt(tx)}
                      className="px-3 py-1.5 bg-purple-50 dark:bg-purple-900/30 hover:bg-purple-100 text-purple-700 dark:text-purple-300 rounded-lg text-xs font-semibold transition-colors flex items-center space-x-1"
                    >
                      <Printer className="h-3.5 w-3.5" />
                      <span>Print Slip</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop Table View (>= md screen) */}
          <div className="hidden md:block bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-slate-50 dark:bg-gray-700/50 border-b border-slate-200 dark:border-gray-700 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                  <tr>
                    <th className="px-4 py-3">Book Title / ISBN</th>
                    <th className="px-4 py-3">Borrower / USN</th>
                    <th className="px-4 py-3">Copy Barcode</th>
                    <th className="px-4 py-3">Issue Date</th>
                    <th className="px-4 py-3">Due Date</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Fine</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-gray-700 text-xs sm:text-sm">
                  {transactions.map((tx) => {
                    const status = getStatusBadge(tx.status);
                    const StatusIcon = status.icon;
                    const isOverdue = tx.status === 'overdue' || (tx.status === 'issued' && new Date(tx.dueDate) < new Date());

                    return (
                      <tr key={tx._id} className="hover:bg-slate-50/70 dark:hover:bg-gray-700/50 transition-colors">
                        <td className="px-4 py-3">
                          <p className="font-semibold text-gray-900 dark:text-white truncate max-w-[200px]">{tx.bookTitle || 'Untitled'}</p>
                          <p className="text-xs text-slate-400">ISBN: {tx.bookISBN || 'N/A'}</p>
                        </td>

                        <td className="px-4 py-3">
                          <p className="font-semibold text-gray-900 dark:text-white truncate max-w-[160px]">{tx.memberName || 'Unknown'}</p>
                          <p className="text-xs text-slate-500 font-mono">{tx.memberUSN || tx.memberEmail || 'N/A'}</p>
                        </td>

                        <td className="px-4 py-3">
                          <span className="font-mono text-xs bg-slate-100 dark:bg-gray-700 px-2 py-1 rounded text-slate-700 dark:text-slate-300">
                            {tx.bookBarcode || 'N/A'}
                          </span>
                        </td>

                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300">
                          {new Date(tx.issueDate).toLocaleDateString('en-IN')}
                        </td>

                        <td className="px-4 py-3">
                          <span className={`font-semibold ${isOverdue ? 'text-rose-600 dark:text-rose-400' : 'text-slate-700 dark:text-slate-300'}`}>
                            {new Date(tx.dueDate).toLocaleDateString('en-IN')}
                          </span>
                        </td>

                        <td className="px-4 py-3">
                          <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border flex items-center w-fit space-x-1 ${status.color}`}>
                            <StatusIcon className="h-3 w-3" />
                            <span>{status.label}</span>
                          </span>
                        </td>

                        <td className="px-4 py-3">
                          <span className={`font-semibold ${tx.fineAmount > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-400'}`}>
                            {tx.fineAmount > 0 ? `₹${tx.fineAmount}` : '-'}
                          </span>
                          {tx.finePaid && (
                            <span className="ml-1 text-xs text-emerald-600 font-semibold">(Paid)</span>
                          )}
                        </td>

                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => printReceipt(tx)}
                            className="p-2 text-slate-500 hover:text-purple-600 dark:text-slate-400 dark:hover:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg transition-colors"
                            title="Print Slip"
                          >
                            <Printer className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Responsive Pagination Controls */}
          {pagination.pages > 1 && (
            <div className="flex flex-col sm:flex-row justify-between items-center p-4 bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 gap-3">
              <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                Showing {(pagination.page - 1) * pagination.limit + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} records
              </span>

              <div className="flex items-center space-x-2">
                <button 
                  onClick={() => fetchTransactionHistory(pagination.page - 1)} 
                  disabled={pagination.page <= 1} 
                  className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors text-xs font-semibold flex items-center space-x-1"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span>Prev</span>
                </button>

                <span className="px-3 py-1.5 rounded-lg bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300 text-xs font-bold">
                  {pagination.page} / {pagination.pages}
                </span>

                <button 
                  onClick={() => fetchTransactionHistory(pagination.page + 1)} 
                  disabled={pagination.page >= pagination.pages} 
                  className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors text-xs font-semibold flex items-center space-x-1"
                >
                  <span>Next</span>
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Empty State */
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 p-6">
          <FileText className="h-12 w-12 text-slate-300 dark:text-gray-600 mx-auto mb-3" />
          <h3 className="text-base font-bold text-gray-900 dark:text-white">No Circulation Records Found</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">There are no transaction records matching your query criteria.</p>
          <button 
            onClick={clearFilters} 
            className="mt-4 px-4 py-2 text-xs font-semibold text-purple-600 dark:text-purple-400 hover:underline"
          >
            Clear active filters
          </button>
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;